import React, { useRef } from "react";

import { faCheck, faSort } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonGroup, MenuItem, Popover } from "@material-ui/core";
import styled from "styled-components/macro";

import ButtonComponent from "components/Button/Button";
import { SecondaryStyles } from "components/Button/Styles";

import { useBoolean } from "hooks/useBoolean";
import { useIsDesktop } from "hooks";

export interface SortButtonOption {
  label: string;
  value: string;
}

interface SortButtonProps {
  options: readonly SortButtonOption[];
  value: string;
  onChange: (value: string) => void;
  popoverHeaderLabel?: string;
  // Unique per screen, so tour ids don't collide across consumers.
  dataTour?: string;
}

const FaceButton = styled(ButtonComponent)`
  ${SecondaryStyles}
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[1]}px;
  white-space: nowrap;
`;

const ChevronButton = styled(ButtonComponent)`
  ${SecondaryStyles}
  padding: 0 ${({ theme }) => theme.space[2]}px;
`;

const SplitButtonGroup = styled(ButtonGroup)`
  ${FaceButton} {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }

  ${ChevronButton} {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &:hover {
    ${FaceButton},
    ${ChevronButton} {
      background-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.white};
    }
  }
`;

const PopoverContent = styled.div`
  width: 268px;
`;

const PopoverHeader = styled.div(
  ({ theme }) => `
  font-size: ${theme.fontSizes.alpha}px;
  font-weight: ${theme.fontWeights.bold};
  text-transform: uppercase;
  color: ${theme.colors.gray44};
  padding: ${theme.space[2]}px ${theme.space[3]}px ${theme.space[1]}px;
`,
);

const OptionRow = styled(MenuItem)<{ isSelected: boolean }>(
  ({ theme, isSelected }) => `
  gap: ${theme.space[2]}px;
  padding: ${theme.space[2]}px ${theme.space[3]}px;
  font-size: 15px;
  font-weight: ${isSelected ? theme.fontWeights.bold : theme.fontWeights.normal};
  color: ${theme.colors.gray13};
  ${
    isSelected
      ? `background: ${theme.colors.primarySelectedOverlay};
    box-shadow: inset 3px 0 0 0 ${theme.colors.primary};`
      : ""
  }

  &:hover {
    background: ${theme.colors.primarySelectedOverlay};
  }
`,
);

const CheckmarkColumn = styled.div(
  ({ theme }) => `
  width: 14px;
  color: ${theme.colors.primary};
`,
);

// Shows the current sort choice; the chevron opens a popover to pick another.
export function SortButton({
  options,
  value,
  onChange,
  popoverHeaderLabel = "SORT BY",
  dataTour = "sortButton",
}: SortButtonProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, open, close] = useBoolean(false);
  const isDesktop = useIsDesktop();

  const selectedOption = options.find(option => option.value === value);
  const faceLabel = selectedOption
    ? isDesktop
      ? `Sorted by ${selectedOption.label}`
      : selectedOption.label
    : "";

  function handleSelect(optionValue: string) {
    onChange(optionValue);
    close();
  }

  return (
    <>
      <SplitButtonGroup variant="outlined" ref={anchorRef} data-tour={dataTour}>
        <FaceButton type="button" inline onClick={open}>
          <FontAwesomeIcon icon={faSort} />
          {faceLabel}
        </FaceButton>
        <ChevronButton
          type="button"
          inline
          onClick={open}
          data-tour={`${dataTour}Chevron`}
        >
          <FontAwesomeIcon icon={faChevronDown} size="xs" />
        </ChevronButton>
      </SplitButtonGroup>
      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        marginThreshold={1}
        data-tour={`${dataTour}Popover`}
      >
        <PopoverContent>
          <PopoverHeader>{popoverHeaderLabel}</PopoverHeader>
          {options.map(option => {
            const isSelected = option.value === value;
            return (
              <OptionRow
                key={option.value}
                isSelected={isSelected}
                onClick={() => handleSelect(option.value)}
                data-tour={option.label}
              >
                <CheckmarkColumn>
                  {isSelected ? <FontAwesomeIcon icon={faCheck} size="xs" /> : null}
                </CheckmarkColumn>
                <div>{option.label}</div>
              </OptionRow>
            );
          })}
        </PopoverContent>
      </Popover>
    </>
  );
}

export default SortButton;
